//import i18next from 'i18next';
import { RootStore } from './rootStore';
import { APP_CONFIG } from 'appConfig';
import { COMMON_ENDPOINTS, NON_VERSIONED_ENDPOINTS, AUTO_ENDPOINTS } from 'api/endpoints';
import { Permission } from '../helpers/permissions';

const download = require('downloadjs');

export const GLOBAL_ERR_PREFIX = 'g_';

export enum Method {
  GET = 'get',
  POST = 'post',
  DELETE = 'delete',
  PATCH = 'PATCH', // Musi byt uppercase: https://github.com/github/fetch/issues/37
}

export interface ApiResponseError {
  type: string;
  key: string;
  message: string;
  params?: any;
  occurrenceId?: string;
  timestamp?: string;
}
export interface ApiResponse<T> {
  data?: T;
  errors?: ApiResponseError[];
  params?: any;
  status?: number;
}

interface CacheableEndpoint {
  re: RegExp;
  validFor: number;
}

interface CachedResponse<T> {
  cachedAt: number;
  response: ApiResponse<T>;
}

export class ApiStore {
  private rootStore: RootStore;

  /* eslint-disable no-useless-escape */
  private cacheableEndpoints: CacheableEndpoint[] = [
    {
      re: /\/account\/[^\/]*\/detail\/plan(.*)/,
      validFor: 0, // 0s means infinty, the cached version needs to be invalidated MANUALY
    },
    {
      re: /\/account\/[^\/]*\/detail\/history(.*)/,
      validFor: 0,
    },
    {
      re: /\/account\/active/,
      validFor: 0,
    },
    {
      re: /\/account\/inactive/,
      validFor: 0,
    },
  ];
  /* eslint-enable no-useless-escape */

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  endpointUrl(endpoint: string): string {
    /*
      FIXME: EDS-4783 hotfix workaround, API_ASSET_CHART je metoda s parametry skladajici url, 
      tudiz neni realny vystup k dispozici v `endpoints.ts` aby se tu staticky porovnalo oproti COMMON_ENDPOINTS
    */
    const endpointParts = endpoint.split('/');
    const apiAssetChartUrl: boolean =
      endpointParts[1] === 'product' && endpointParts[3].indexOf('history?period') === 0;

    const usePrefix =
      this.rootStore.stores.userStore.checkPermissions(Permission.APP_ENDUSER) &&
      !COMMON_ENDPOINTS.includes(endpoint) &&
      !apiAssetChartUrl;

    const prefix = usePrefix ? APP_CONFIG.enduserPrefix : '';

    const version = !NON_VERSIONED_ENDPOINTS.includes(endpoint) ? `/${APP_CONFIG.apiVersion}` : '';
    return `${APP_CONFIG.apiUrl}${version}${prefix}${endpoint}`;
  }

  get authToken(): string {
    return this.rootStore.stores.userStore.getToken();
  }

  get = async <T>(endpoint: string, ignoreErrors?: boolean | number[]) => {
    return await this.handleCacheableResponse<T>(endpoint, Method.GET, ignoreErrors);
  };

  post = async <T>(endpoint: string, data?: any, noFormValidation?: boolean, ignoreErrors?: boolean | number[]) => {
    return await this.handleRequest<T>(
      endpoint,
      Method.POST,
      typeof data !== undefined ? data : undefined,
      undefined,
      noFormValidation,
      ignoreErrors,
    );
  };

  delete = async <T>(endpoint: string, data?: any) => {
    return await this.handleRequest<T>(endpoint, Method.DELETE, typeof data !== undefined ? data : undefined);
  };

  patch = async <T>(endpoint: string, data?: any, noFormValidation?: boolean, ignoreErrors?: boolean | number[]) => {
    return await this.handleRequest<T>(
      endpoint,
      Method.PATCH,
      typeof data !== undefined ? data : undefined,
      undefined,
      noFormValidation,
      ignoreErrors,
    );
  };

  getFilename = (extension: string, disposition?: any) => {
    let filename = `download.${extension}`;
    if (disposition) {
      // disposition format:
      // attachment; filename*=UTF-8''Cen%C3%ADk%20slu%C5%BEeb%20od%2030.04.2020.pdf
      const re = new RegExp(`filename\\*=UTF-8''(.+\\.${extension})`);
      const parsed = re.exec(disposition);

      if (parsed && parsed[1]) {
        filename = decodeURIComponent(parsed[1]);
      }
    }
    return filename;
  };

  loadFromCache = <T>(endpoint: string): CachedResponse<T> | null => {
    const cachedData = sessionStorage.getItem(endpoint);
    if (!cachedData) {
      return null;
    }

    try {
      return JSON.parse(cachedData);
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  saveToCache = <T>(endpoint: string, response: ApiResponse<T>, validFor?: number) => {
    sessionStorage.setItem(
      endpoint,
      JSON.stringify({
        cachedAt: new Date().getTime(),
        validFor: validFor || 0,
        response: response,
      }),
    );
  };

  removeFromCache = (endpoint: string) => {
    sessionStorage.removeItem(endpoint);
  };

  isCachedResponseValid = (response: CachedResponse<any>, endpoint: CacheableEndpoint): boolean => {
    switch (endpoint.validFor) {
      case 0: // 0 means infinity
        return true;
      default:
        return new Date().getTime() - response.cachedAt < endpoint.validFor;
    }
  };

  handleCacheableResponse = async <T>(
    endpoint: string,
    method: Method,
    ignoreErrors?: boolean | number[],
  ): Promise<ApiResponse<T> | null> => {
    const cacheableEndpoint = this.cacheableEndpoints.find((ce) => {
      return ce.re.test(endpoint);
    });

    if (cacheableEndpoint) {
      const cachedResponse = this.loadFromCache<T>(endpoint);

      if (cachedResponse && this.isCachedResponseValid(cachedResponse, cacheableEndpoint)) {
        new Promise((resolve) => {
          resolve(cachedResponse.response);
        });
      } else {
        return this.handleRequest<T>(endpoint, method, undefined, undefined, undefined, ignoreErrors).then(
          (response) => {
            if (!response || response.errors) {
              return null;
            }
            this.saveToCache(endpoint, response, cacheableEndpoint.validFor);
            return response;
          },
        );
      }
    }

    return this.handleRequest(endpoint, method, undefined, undefined, undefined, ignoreErrors);
  };
  /*
  keyInFields = (key: string, fields: string[]) => {
    const depth = (fields[0].match(/prefix\./g) || []).length;
    const keyPath = key.split('.');
    const trimmedKey = keyPath.slice(depth, keyPath.length - 1).join('.');
    return fields.findIndex((field) => field.split('.').slice(depth).join('.') === trimmedKey) > -1;
  };
  */

  getResponseBody = async <T>(
    response: Response,
    noFormValidation?: boolean,
    ignoreErrors?: boolean | number[],
  ): Promise<ApiResponse<T> | null> => {
    const { addGlobalError } = this.rootStore.stores.errorStore;

    switch (response.status) {
      case 204: // no data
        return { status: 204 };
      case 403:
        addGlobalError('globalErrors_forbidden');
        return { status: 403 };
    }

    const body: ApiResponse<T> = await response.json();

    body.status = response.status;

    if (
      !body.errors ||
      ignoreErrors === true ||
      (Array.isArray(ignoreErrors) && ignoreErrors.includes(response.status))
    ) {
      return body;
    }

    let validationSet = false;

    (body.errors || []).forEach((error) => {
      // Do not report validation errors as global errors. The will be handled by formik.
      //console.log(error.key, this.keyInFields(error.key, formFields!));

      if (error.key === 'SRV_INTERNAL_ERROR') {
        this.rootStore.stores.errorStore.addGenericGlobalError();
      } else if (error.key.substr(0, GLOBAL_ERR_PREFIX.length) === GLOBAL_ERR_PREFIX) {
        addGlobalError(`globalErrors_${error.key.substr(GLOBAL_ERR_PREFIX.length)}`);
      } else {
        if (!noFormValidation) {
          if (!validationSet) {
            addGlobalError('globalErrors_validation');
            validationSet = true;
          }
          this.rootStore.stores.errorStore.disableAutoHide();
          return;
        } else {
          this.rootStore.stores.errorStore.disableAutoHide();
        }
      }
      /*
      if (
        error.type &&
        //TODO: remove keyInFields logic (also formStore)
        //error.type === 'ValidationException' && (!formFields || this.keyInFields(error.key, formFields))
        error.type === 'ValidationException' &&
        !i18next.exists(`globalErrors_${error.key}`)
      ) {
        if (!noFormValidation) {
          if (!validationSet) {
            addGlobalError('globalErrors_validation');
            validationSet = true;
          }
          return;
        } else {
          this.rootStore.stores.errorStore.disableAutoHide();
        }
      }

      addGlobalError(`globalErrors_${error.key}`);
      */
    });

    return body;
  };

  handleRequest = async <T>(
    endpoint: string,
    method: Method,
    data?: any,
    init?: RequestInit, // FIXME: unused?
    noFormValidation?: boolean,
    ignoreErrors?: boolean | number[],
    formFields?: string[],
  ): Promise<ApiResponse<T> | null> => {
    const { addGenericGlobalError } = this.rootStore.stores.errorStore;

    try {
      const response = await fetch(
        this.endpointUrl(endpoint),
        init || {
          method: method,
          body: data !== undefined ? JSON.stringify(data) : undefined,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.authToken}`,
          },
        },
      );

      if (response?.status === 401) {
        console.error('Unauthorized - Bearer:', this.authToken);
        this.rootStore.stores.userStore.logout();
      }

      if (!AUTO_ENDPOINTS.includes(endpoint)) {
        this.rootStore.stores.userStore.resetSessionTimers();
        this.rootStore.stores.uiStore.sendRefreshToNotes();
      }

      const contentTypeHeader = response.headers.get('content-type') || '';
      const contentTypeMatch = contentTypeHeader.match(/^([^/]+)\/([^;]+)(;.+=.+)?$/);

      if (!contentTypeMatch) {
        console.warn(`Unsupported content type: ${contentTypeHeader}`);
        return null;
      }

      const contentType = contentTypeMatch[2];

      switch (contentType) {
        case 'json':
          return await this.getResponseBody(response, noFormValidation, ignoreErrors);
        case 'pdf':
        case 'txt':
        case 'csv':
        case 'jpeg':
        case 'png':
          const disposition = response.headers.get('content-disposition');
          const blob = await response.blob();
          const filename = this.getFilename(contentType, disposition);
          download(blob, filename, contentTypeHeader);

          return { status: response.status };
        default:
          console.warn(`Unsupported content type: ${contentTypeHeader}`);
          return null;
      }
    } catch (error) {
      addGenericGlobalError();
      console.error(error);
      return null;
    }
  };
}
